import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import axios from 'axios';
import {
  Calculator, DollarSign, TrendingUp, TrendingDown,
  Moon, Sun, Lock, Settings, ChevronDown, Check, X, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TESTING MODE - Set to false for production
const TESTING_MODE = true;

// Platform fee structures (2025 estimates)
const PLATFORMS = {
  ebay: {
    id: 'ebay',
    name: 'eBay',
    icon: '🛒',
    free: true,
    // 13% final value fee + $0.40 per order
    calculateFees: (salePrice, shippingCharged) => {
      const total = salePrice + shippingCharged;
      return (total * 0.13) + 0.40;
    },
    feeDescription: '13% + $0.40'
  },
  mercari: {
    id: 'mercari',
    name: 'Mercari',
    icon: '🔴',
    free: false,
    // 10% selling fee on item price + buyer-paid shipping
    calculateFees: (salePrice, shippingCharged) => {
      const total = salePrice + shippingCharged;
      return total * 0.10;
    },
    feeDescription: '10% fee'
  },
  poshmark: {
    id: 'poshmark',
    name: 'Poshmark',
    icon: '👗',
    free: false,
    // $2.95 flat for sales < $15; 20% for sales ≥ $15
    calculateFees: (salePrice) => {
      if (salePrice < 15) return 2.95;
      return salePrice * 0.20;
    },
    feeDescription: '$2.95 (<$15) or 20%'
  },
  depop: {
    id: 'depop',
    name: 'Depop',
    icon: '🌸',
    free: false,
    // ~3.3% + $0.45 payment processing (no 10% marketplace fee for US/USD)
    calculateFees: (salePrice) => {
      return (salePrice * 0.033) + 0.45;
    },
    feeDescription: '3.3% + $0.45'
  },
  stockx: {
    id: 'stockx',
    name: 'StockX',
    icon: '📈',
    free: false,
    // 9% seller fee (Level 1) + 3% payment processing
    calculateFees: (salePrice) => {
      return salePrice * 0.12; // 9% + 3%
    },
    feeDescription: '9% + 3% processing'
  }
};

const PLATFORM_VISIBILITY_KEY = 'flipvalue_platform_visibility';

const getDefaultVisibility = () => ({
  ebay: true,
  mercari: false,
  poshmark: false,
  depop: false,
  stockx: false
});

export default function ProfitCalculatorPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, token } = useAuth();
  const { recordAction } = useGuest();
  const mainContentRef = useRef(null);
  
  const prefillData = location.state || {};

  const [selectedPlatform, setSelectedPlatform] = useState('ebay');
  const [salePrice, setSalePrice] = useState(prefillData.estimatedValue?.toString() || '');
  const [costPrice, setCostPrice] = useState(prefillData.costPrice?.toString() || '');
  const [shippingCharged, setShippingCharged] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [showShipping, setShowShipping] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false); // Track if user clicked Calculate
  const [calcsUsedThisSession, setCalcsUsedThisSession] = useState(0);
  
  // User tier logic
  const isGuest = !user;
  const userTier = user?.subscription?.tier || 'free';
  const isFreeUser = userTier === 'free';
  const isBasicUser = userTier === 'basic';
  const isProUser = userTier === 'pro' || userTier === 'lifetime_pro';
  
  // isRestricted = guest or free (for platform locking and other restrictions)
  const isRestricted = isGuest || isFreeUser;
  
  // For Free users: blur break-even ALWAYS (it's a paid feature)
  // For Basic/Pro: always show results
  const shouldBlurBreakEven = isFreeUser;
  const shouldBlurResults = isFreeUser && !hasCalculated;
  
  // Platform restriction: Guest/Free = eBay only, Basic = 1 platform, Pro = all
  const isPlatformLocked = isGuest || isFreeUser || isBasicUser;
  
  // Handle Calculate button click
  const handleCalculate = useCallback(async () => {
    if (!salePrice || !costPrice) return;
    
    setHasCalculated(true);
    
    // Track calculation for logged-in users
    if (!isGuest && token) {
      try {
        await axios.post(
          `${API}/usage/track?usage_type=profit_calcs_used`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCalcsUsedThisSession(prev => prev + 1);
      } catch (error) {
        console.error('Failed to track usage:', error);
      }
    }
  }, [isGuest, token, salePrice, costPrice]);
  
  // Handle Enter key to trigger calculate
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCalculate();
    }
  };
  
  // Platform visibility state
  const [platformVisibility, setPlatformVisibility] = useState(() => {
    try {
      const stored = localStorage.getItem(PLATFORM_VISIBILITY_KEY);
      if (stored) return JSON.parse(stored);
    } catch {
      // Ignore parse errors, return default
    }
    return getDefaultVisibility();
  });

  // Save visibility to localStorage
  useEffect(() => {
    localStorage.setItem(PLATFORM_VISIBILITY_KEY, JSON.stringify(platformVisibility));
  }, [platformVisibility]);

  const visiblePlatforms = Object.entries(PLATFORMS)
    .filter(([key]) => platformVisibility[key])
    .map(([, platform]) => platform);

  const platform = PLATFORMS[selectedPlatform];

  // Calculate results for all platforms
  const calculateAllPlatforms = () => {
    const salePriceNum = parseFloat(salePrice) || 0;
    const costPriceNum = parseFloat(costPrice) || 0;
    const shippingChargedNum = parseFloat(shippingCharged) || 0;
    const shippingCostNum = parseFloat(shippingCost) || 0;
    const totalCost = costPriceNum + shippingCostNum;

    return visiblePlatforms.map(p => {
      const fees = p.calculateFees(salePriceNum, shippingChargedNum);
      const netFromSale = salePriceNum + shippingChargedNum - fees;
      const profit = netFromSale - totalCost;
      
      // Break-even calculation
      let breakEven = 0;
      if (totalCost > 0) {
        // Specific calculations for each platform
        if (p.id === 'poshmark') {
          // $2.95 flat for sales < $15; 20% for sales ≥ $15
          if (totalCost < 15 - 2.95) {
            breakEven = totalCost + 2.95;
          } else {
            breakEven = totalCost / 0.80;
          }
        } else if (p.id === 'ebay') {
          // 13% + $0.40 (approx)
          breakEven = (totalCost + 0.40) / 0.87;
        } else if (p.id === 'depop') {
          // 3.3% + $0.45
          breakEven = (totalCost + 0.45) / 0.967;
        } else if (p.id === 'mercari') {
          // 10% fee on total
          breakEven = totalCost / 0.90;
        } else if (p.id === 'stockx') {
          // 12% total (9% seller + 3% processing)
          breakEven = totalCost / 0.88;
        } else {
          // Generic calculation
          const feeRate = fees / (salePriceNum || 1);
          breakEven = totalCost / (1 - Math.min(feeRate, 0.99));
        }
      }

      return {
        ...p,
        fees,
        netFromSale,
        profit,
        breakEven,
        isUnlocked: p.free || TESTING_MODE
      };
    });
  };

  const allResults = calculateAllPlatforms();
  const currentResult = allResults.find(r => r.id === selectedPlatform);

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  const handlePlatformSelect = (platformKey) => {
    setSelectedPlatform(platformKey);
    setShowPlatformDropdown(false);
  };

  const togglePlatformVisibility = (platformKey) => {
    setPlatformVisibility(prev => {
      const newVisibility = { ...prev, [platformKey]: !prev[platformKey] };
      // Ensure at least 1 platform is enabled
      const enabledCount = Object.values(newVisibility).filter(Boolean).length;
      if (enabledCount === 0) {
        return prev; // Don't allow disabling the last platform
      }
      return newVisibility;
    });
  };

  // Count enabled platforms for UI feedback
  const enabledPlatformCount = Object.values(platformVisibility).filter(Boolean).length;

  const hasValues = parseFloat(costPrice) > 0;

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="w-full px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NavigationMenu />
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="text-left">
                <h1 className="text-base font-bold leading-tight">FlipValue</h1>
                <p className="text-[10px] text-muted-foreground -mt-0.5">Profit Calculator</p>
              </div>
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="w-full px-3 py-2 flex-1" ref={mainContentRef}>
        {/* Sticky Top Bar - Break-Even only */}
        <div className="sticky top-14 z-20 -mx-3 px-3 py-2 bg-primary text-primary-foreground shadow-sm mb-3">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-xs opacity-80">Break-Even:</span>
              {(isGuest || shouldBlurBreakEven) ? (
                <span className="text-lg font-bold font-mono blur-sm select-none">$XX.XX</span>
              ) : (
                <span className="text-lg font-bold font-mono">
                  {formatCurrency(currentResult?.breakEven || 0)}
                </span>
              )}
            </div>
            {(isGuest || shouldBlurBreakEven) && (
              <span className="text-[10px] opacity-70">
                <Lock className="w-3 h-3 inline mr-1" />
                {isGuest ? 'Sign up to see' : 'Upgrade to Pro'}
              </span>
            )}
          </div>
        </div>

        {/* Pre-fill notice - Condensed */}
        {prefillData.itemName && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-2 mb-3">
            <p className="text-xs">
              <span className="font-medium">Calculating for:</span> {prefillData.itemName}
            </p>
          </div>
        )}

        {/* Platform Selector - Condensed */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold">Platform</label>
            {!isPlatformLocked && (
              <button
                onClick={() => setShowCustomizeModal(true)}
                className="flex items-center gap-1 text-xs text-primary hover:underline font-medium"
              >
                <Settings className="w-3.5 h-3.5" />
                Customize
              </button>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => !isPlatformLocked && setShowPlatformDropdown(!showPlatformDropdown)}
              disabled={isPlatformLocked}
              className={`w-full flex items-center justify-between p-2 rounded-lg border border-border bg-card transition-colors ${
                isPlatformLocked ? 'opacity-70 cursor-not-allowed' : 'hover:border-primary/50'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{platform.icon}</span>
                <div className="text-left">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-sm">{platform.name}</span>
                    {isPlatformLocked && (
                      <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-600 font-medium flex items-center gap-0.5">
                        <Lock className="w-2 h-2" />
                        {isProUser ? '' : isBasicUser ? 'Change in Settings' : 'eBay Only'}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground">{platform.feeDescription}</p>
                </div>
              </div>
              {!isPlatformLocked && (
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showPlatformDropdown ? 'rotate-180' : ''}`} />
              )}
            </button>

            {/* Dropdown - only show for Pro users */}
            {showPlatformDropdown && !isPlatformLocked && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-lg z-50 overflow-hidden max-h-80 overflow-y-auto">
                {visiblePlatforms.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handlePlatformSelect(p.id)}
                    className={`w-full flex items-center justify-between p-3 hover:bg-secondary/50 transition-colors ${
                      selectedPlatform === p.id ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{p.icon}</span>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{p.name}</span>
                          {p.free ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-600 font-medium">FREE</span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 font-medium flex items-center gap-0.5">
                              <Lock className="w-2.5 h-2.5" />
                              PREMIUM
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{p.feeDescription}</p>
                      </div>
                    </div>
                    {selectedPlatform === p.id && <Check className="w-4 h-4 text-primary" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Input Section - Condensed */}
        <div className="bg-card rounded-xl border border-border p-3 mb-3">
          <h2 className="font-bold mb-2 flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-primary" />
            Enter Details
          </h2>

          <div className="space-y-2">
            {/* Two inputs side by side on mobile */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium mb-1 block">Your Cost *</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={costPrice}
                    onChange={(e) => setCostPrice(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="0.00"
                    className="pl-7 h-10 text-base font-medium"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium mb-1 block">Sale Price *</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    enterKeyHint="done"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="0.00"
                    className="pl-7 h-10 text-base font-medium"
                  />
                </div>
              </div>
            </div>

            {/* Calculate Profit Button - prominent green */}
            <Button
              onClick={handleCalculate}
              disabled={!salePrice || !costPrice || isGuest}
              className="w-full h-12 bg-green-600 hover:bg-green-700 text-white text-lg font-bold shadow-md"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calculate Profit
            </Button>
            
            {isGuest && (
              <p className="text-center text-xs text-muted-foreground -mt-2">
                <Lock className="w-3 h-3 inline mr-1" />
                <button onClick={() => navigate('/auth?mode=signup')} className="text-primary hover:underline">
                  Sign up free
                </button> to use the profit calculator
              </p>
            )}

            {/* Inline profit preview - blur for Free users until Calculate clicked */}
            <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Est. Profit:</span>
                {shouldBlurResults ? (
                  <span className="text-lg font-bold font-mono text-muted-foreground blur-sm select-none">
                    $XX.XX
                  </span>
                ) : (
                  <span className={`text-lg font-bold font-mono ${(currentResult?.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(currentResult?.profit || 0) >= 0 ? '+' : ''}{formatCurrency(currentResult?.profit || 0)}
                  </span>
                )}
              </div>
              {shouldBlurResults && (
                <p className="text-[10px] text-center text-muted-foreground mt-1">
                  <Lock className="w-3 h-3 inline mr-1" />
                  Click Calculate Profit to see results
                </p>
              )}
              {isGuest && (
                <p className="text-[10px] text-center text-muted-foreground mt-1">
                  <Lock className="w-3 h-3 inline mr-1" />
                  Sign up to see profit calculations
                </p>
              )}
            </div>

            {/* Optional Shipping Toggle */}
            <button
              type="button"
              onClick={() => setShowShipping(!showShipping)}
              className="w-full flex items-center justify-between py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <span className="font-medium">Add shipping costs (optional)</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showShipping ? 'rotate-180' : ''}`} />
            </button>

            {showShipping && (
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
                <div>
                  <label className="text-[10px] text-muted-foreground">Shipping Charged</label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={shippingCharged}
                      onChange={(e) => setShippingCharged(e.target.value)}
                      placeholder="0.00"
                      className="pl-6 h-8 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Your Ship Cost</label>
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      enterKeyHint="done"
                      value={shippingCost}
                      onChange={(e) => setShippingCost(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      placeholder="0.00"
                      className="pl-6 h-8 text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Multi-Platform Comparison */}
        {hasValues && (
          <div className="mb-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
              <Calculator className="w-4 h-4 text-primary" />
              Compare Across Platforms
            </h3>
            
            <div className="space-y-2">
              {allResults.map((result) => {
                const isLocked = !result.isUnlocked;
                
                return (
                  <div
                    key={result.id}
                    className={`bg-card rounded-xl border p-4 transition-all ${
                      selectedPlatform === result.id 
                        ? 'border-primary ring-2 ring-primary/20' 
                        : 'border-border'
                    }`}
                    onClick={() => handlePlatformSelect(result.id)}
                  >
                    <div className="flex items-center gap-3">
                      {/* Platform Icon & Name */}
                      <div className="flex items-center gap-2 min-w-[100px]">
                        <span className="text-lg">{result.icon}</span>
                        <div>
                          <span className="font-medium text-sm">{result.name}</span>
                          {!result.free && !TESTING_MODE && (
                            <Lock className="w-3 h-3 text-amber-500 inline ml-1" />
                          )}
                        </div>
                      </div>

                      {/* Results - blur for guests, blur for locked premium platforms */}
                      <div className={`flex-1 grid grid-cols-3 gap-2 text-center ${(isLocked && !TESTING_MODE) || isGuest ? 'blur-sm opacity-50 select-none' : ''}`}>
                        <div className={shouldBlurBreakEven && !isGuest ? 'blur-sm opacity-50 select-none' : ''}>
                          <p className="text-[10px] text-muted-foreground">Break-Even</p>
                          <p className="text-sm font-bold font-mono">{formatCurrency(result.breakEven)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground">Fees</p>
                          <p className="text-sm font-mono text-red-500">-{formatCurrency(result.fees)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground">Profit</p>
                          <p className={`text-sm font-bold font-mono ${result.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {result.profit >= 0 ? '+' : ''}{formatCurrency(result.profit)}
                          </p>
                        </div>
                      </div>

                      {/* Lock overlay for guests */}
                      {isGuest && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-xl">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Lock className="w-3 h-3" />
                            <span>Sign up to view</span>
                          </div>
                        </div>
                      )}
                      {/* Lock overlay for premium (non-guests) */}
                      {!isGuest && isLocked && !TESTING_MODE && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-xl">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Lock className="w-3 h-3" />
                            <span>Upgrade to unlock</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {!TESTING_MODE && (
              <p className="text-[10px] text-muted-foreground text-center mt-2">
                Premium platforms show blurred results. Upgrade to see full details.
              </p>
            )}
          </div>
        )}

        {/* Detailed Breakdown for Selected Platform */}
        {hasValues && currentResult && (
          <div className="bg-card rounded-xl border border-border p-4 relative">
            <h3 className="font-semibold mb-3 text-sm flex items-center gap-2">
              {platform.icon} {platform.name} Breakdown
            </h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sale Price</span>
                <span className="font-mono">{formatCurrency(parseFloat(salePrice) || 0)}</span>
              </div>
              {parseFloat(shippingCharged) > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">+ Shipping Charged</span>
                  <span className="font-mono">{formatCurrency(parseFloat(shippingCharged))}</span>
                </div>
              )}
              <div className="flex justify-between text-red-600 dark:text-red-400">
                <span>- {platform.name} Fees ({platform.feeDescription})</span>
                <span className={`font-mono ${isGuest ? 'blur-sm select-none' : ''}`}>-{formatCurrency(currentResult.fees)}</span>
              </div>
              <div className="flex justify-between font-medium border-t border-border pt-2">
                <span>Net from Sale</span>
                <span className={`font-mono ${isGuest ? 'blur-sm select-none' : ''}`}>{formatCurrency(currentResult.netFromSale)}</span>
              </div>
              <div className="flex justify-between text-red-600 dark:text-red-400">
                <span>- Your Cost</span>
                <span className="font-mono">-{formatCurrency(parseFloat(costPrice) || 0)}</span>
              </div>
              {parseFloat(shippingCost) > 0 && (
                <div className="flex justify-between text-red-600 dark:text-red-400">
                  <span>- Shipping Cost</span>
                  <span className="font-mono">-{formatCurrency(parseFloat(shippingCost))}</span>
                </div>
              )}
              <div className={`flex justify-between font-bold text-lg border-t border-border pt-2 ${
                currentResult.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                <span>Profit</span>
                <span className={`font-mono flex items-center gap-1 ${isGuest ? 'blur-sm select-none' : ''}`}>
                  {currentResult.profit >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {formatCurrency(currentResult.profit)}
                </span>
              </div>
            </div>
            
            {/* Sign up prompt for guests */}
            {isGuest && (
              <div className="absolute inset-0 flex items-end justify-center pb-4 pointer-events-none">
                <Button 
                  size="sm" 
                  className="pointer-events-auto"
                  onClick={() => navigate('/auth?mode=signup')}
                >
                  Sign up to see full breakdown
                </Button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Customize Platforms Modal */}
      <Dialog open={showCustomizeModal} onOpenChange={setShowCustomizeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Customize Platforms
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-sm text-muted-foreground mb-4">
            {isRestricted 
              ? 'Only eBay is available in free mode. Upgrade to unlock all platforms.'
              : 'Choose which platforms to show in the calculator. At least 1 platform must be enabled.'
            }
          </p>

          <div className="space-y-3">
            {Object.entries(PLATFORMS).map(([key, p]) => {
              const isEnabled = platformVisibility[key];
              const isLastEnabled = isEnabled && enabledPlatformCount === 1;
              // Lock non-eBay platforms for guest/free users
              const isLockedForFree = isRestricted && key !== 'ebay';
              
              return (
                <div
                  key={key}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isEnabled && !isLockedForFree ? 'border-primary/30 bg-primary/5' : 'border-border'
                  } ${isLockedForFree ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{p.icon}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{p.name}</span>
                        {key === 'ebay' ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-600 font-medium">FREE</span>
                        ) : isRestricted ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 font-medium flex items-center gap-0.5">
                            <Lock className="w-2.5 h-2.5" />
                            PRO
                          </span>
                        ) : p.free ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-600 font-medium">FREE</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 font-medium">PREMIUM</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{p.feeDescription}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {isLockedForFree ? (
                      <span className="text-[10px] text-amber-600 flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        Upgrade
                      </span>
                    ) : isLastEnabled ? (
                      <span className="text-[10px] text-muted-foreground">Min 1</span>
                    ) : null}
                    <Switch
                      checked={isEnabled && !isLockedForFree}
                      onCheckedChange={() => togglePlatformVisibility(key)}
                      disabled={isLastEnabled || isLockedForFree}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {isRestricted && (
            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                <span>Upgrade to Pro to unlock all {Object.keys(PLATFORMS).length} selling platforms!</span>
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowCustomizeModal(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Footer */}
      <Footer />
    </div>
  );
}
